// src/pages/CompanyFormPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companiesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';


const STATUSES = ['CLIENT','ANCIEN_CLIENT','PROSPECT','PROSPECT_CHAUD','PARTENAIRE','ANCIEN_PARTENAIRE','FOURNISSEUR','COLLABORATEUR_EXTERNE','ARCHIVE'];
const STATUS_LABELS = {
  'CLIENT': 'Client',
  'ANCIEN_CLIENT': 'Ancien client',
  'PROSPECT': 'Prospect',
  'PROSPECT_CHAUD': 'Prospect chaud',
  'PARTENAIRE': 'Partenaire',
  'ANCIEN_PARTENAIRE': 'Ancien partenaire',
  'FOURNISSEUR': 'Fournisseur',
  'COLLABORATEUR_EXTERNE': 'Collaborateur externe',
  'ARCHIVE': 'Archivé'
};


export default function CompanyFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = !!id;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name:'', status:'PROSPECT', sector:'', city:'', country:'', website:'',
    phone:'', email:'', siret:'', description:'', manager_name:'', account_owner_name:'',
    logo_url:'',
  });


  useEffect(() => {
    if (!isEdit) return;
    companiesAPI.getOne(id).then(r => setForm(r.data)).catch(() => { toast.error('Erreur'); navigate('/companies'); });
  }, [id]);


  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Nom obligatoire'); return; }
    setSaving(true);
    try {
      if (isEdit) await companiesAPI.update(id, form);
      else        await companiesAPI.create(form);
      toast.success(isEdit ? 'Société mise à jour' : 'Société créée');
      navigate(isEdit ? `/companies/${id}` : '/companies');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };


  return (
    <div>
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(isEdit ? `/companies/${id}` : '/companies')}><ArrowLeft size={18}/></button>
          <div>
            <h1>{isEdit ? 'Modifier la société' : 'Nouvelle société'}</h1>
            <p>{isEdit ? form.name : 'Ajouter un client ou prospect'}</p>
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate(isEdit ? `/companies/${id}` : '/companies')}>Annuler</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSubmit}>
            {saving ? <div className="spinner" style={{ width:14, height:14 }}/> : <Save size={14}/>} {isEdit ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </div>


      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Informations générales</span></div>
          <div style={{ display:'grid', gap:14 }}>
            {/* Logo preview */}
            <div>
              <label style={{ display:'block', fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Logo URL</label>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input style={inp} value={form.logo_url||''} onChange={set('logo_url')} placeholder="https://logo.clearbit.com/societe.com"/>
                {form.logo_url && (
                  <img src={form.logo_url} alt="logo" onError={e => e.target.style.display='none'}
                    style={{ width:40, height:40, objectFit:'contain', borderRadius:8, border:'1px solid var(--border)', padding:4, flexShrink:0 }}/>
                )}
              </div>
            </div>
            <Field label="Nom de la société *" value={form.name} onChange={set('name')} placeholder="Ex: Société Générale"/>
            <Field label="Statut" type="select" value={form.status} onChange={set('status')} options={STATUSES} optionLabels={STATUS_LABELS}/>
            <Field label="Secteur" value={form.sector} onChange={set('sector')} placeholder="Banque, IT, Industrie…"/>
            <Field label="Description / Notes" type="textarea" value={form.description} onChange={set('description')} placeholder="Contexte, historique…"/>
          </div>
        </div>


        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Coordonnées</span></div>
            <div style={{ display:'grid', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Ville" value={form.city} onChange={set('city')} placeholder="Paris"/>
                <Field label="Pays" value={form.country} onChange={set('country')} placeholder="France"/>
              </div>
              <Field label="Site web" value={form.website} onChange={set('website')} placeholder="https://…"/>
              <Field label="Email" type="email" value={form.email} onChange={set('email')}/>
              <Field label="Téléphone" value={form.phone} onChange={set('phone')}/>
              <Field label="SIRET" value={form.siret} onChange={set('siret')}/>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">Responsables</span></div>
            <div style={{ display:'grid', gap:14 }}>
              <Field label="Notre account manager" value={form.manager_name} onChange={set('manager_name')}/>
              <Field label="Interlocuteur principal" value={form.account_owner_name} onChange={set('account_owner_name')}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function Field({ label, type='text', value, onChange, placeholder='', options=[], optionLabels={} }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</label>
      {type === 'select' ? (
        <select style={inp} value={value} onChange={onChange}>
          {options.map(o => <option key={o} value={o}>{optionLabels[o] || o || '—'}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={value || ''} onChange={onChange} placeholder={placeholder}/>
      ) : (
        <input style={inp} type={type} value={value || ''} onChange={onChange} placeholder={placeholder}/>
      )}
    </div>
  );
}
const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
